import { Check } from 'lucide-react'
import { AnimatePresence, motion } from 'motion/react'

import { AvatarWithInitials } from 'src/components'

import {
  AT,
  EASE,
  popIn,
  RING_AVATAR_SIZE,
  RING_SIZE,
  RING_STROKE
} from './constants'
import { useStyles } from './styles'

const RING_RADIUS = RING_SIZE / 2 - RING_STROKE

const BREATHE_SECONDS = 4.5

type Props = {
  workspaceName: string
  photoUrl?: string
  percent: number
  isComplete: boolean
  hasFailed: boolean
  animates: boolean
}

/**
 * The workspace's own mark, inside a ring that draws itself once and then tracks
 * the build. Two rings on one circle: the drawn one is the arrival, the tracking
 * one is the progress — separating them keeps a 0% build from looking like a
 * ring that failed to appear.
 *
 * Breathing sits on an outer layer so it cannot fight the entrance's own scale.
 */
export const BuildRing = ({
  workspaceName,
  photoUrl,
  percent,
  isComplete,
  hasFailed,
  animates
}: Props) => {
  const { styles } = useStyles()

  const stroke = hasFailed
    ? 'var(--ant-color-error)'
    : 'var(--ant-color-primary)'

  return (
    <motion.div
      className={styles.ring}
      animate={animates && !isComplete ? { scale: [1, 1.05] } : undefined}
      transition={
        animates && !isComplete
          ? {
              duration: BREATHE_SECONDS,
              delay: 1.6,
              repeat: Infinity,
              repeatType: 'reverse',
              ease: 'easeInOut'
            }
          : undefined
      }
    >
      <svg
        width={RING_SIZE}
        height={RING_SIZE}
        viewBox={`0 0 ${RING_SIZE} ${RING_SIZE}`}
        fill="none"
        className={styles.ringSvg}
        aria-hidden
      >
        <circle
          cx={RING_SIZE / 2}
          cy={RING_SIZE / 2}
          r={RING_RADIUS}
          stroke="var(--ant-color-fill-secondary)"
          strokeWidth={RING_STROKE}
        />
        <motion.circle
          cx={RING_SIZE / 2}
          cy={RING_SIZE / 2}
          r={RING_RADIUS}
          stroke={stroke}
          strokeWidth={RING_STROKE}
          strokeLinecap="round"
          transform={`rotate(-90 ${RING_SIZE / 2} ${RING_SIZE / 2})`}
          initial={{ pathLength: animates ? 0 : 1 }}
          animate={{
            pathLength: isComplete ? 1 : Math.max(percent / 100, 0.06)
          }}
          transition={
            animates
              ? { duration: 0.8, delay: AT.ringDraw, ease: EASE }
              : { duration: 0 }
          }
        />
      </svg>

      <span className={styles.ringAvatar}>
        {/* Name only, no id: the preview promised a tile derived from the
            name, and this ring is that same tile arriving. */}
        <AvatarWithInitials
          name={workspaceName}
          src={photoUrl}
          size={RING_AVATAR_SIZE}
          initialsFontSize={15}
        />
      </span>

      <AnimatePresence>
        {isComplete && !hasFailed ? (
          <motion.span
            className={styles.ringCheck}
            {...popIn(animates, animates ? AT.ringBadge : 0)}
            exit={{ opacity: 0 }}
          >
            <Check size={12} strokeWidth={3} />
          </motion.span>
        ) : null}
      </AnimatePresence>
    </motion.div>
  )
}
